import { useState } from 'react';
import { useRoomStore } from '../../../stores/roomStore';
import { Button } from '../../common/Button';
import { Input } from '../../common/Input';
import { Avatar } from '../../common/Avatar';
import { useUserProfiles } from '../../../hooks/useUserProfiles';
import { getUserProfileByEmail } from '../../../services/firebase/auth';
import type { PersistentRoom, RoomMember, RoomRole } from '../../../types/room';

interface RoomMembersSectionProps {
  room: PersistentRoom;
  currentUserId: string;
}

/**
 * Section for managing room members.
 */
export function RoomMembersSection({ room, currentUserId }: RoomMembersSectionProps) {
  const { addMember, removeMember, changeMemberRole, error } = useRoomStore();
  const [email, setEmail] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const memberProfiles = useUserProfiles(room.members.map(resolveMemberProfileKey));

  const handleAddMember = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      return;
    }

    setIsAdding(true);
    try {
      const normalizedEmail = trimmedEmail.toLowerCase();
      const profile = await getUserProfileByEmail(normalizedEmail);
      const resolvedUserId = profile?.uid ?? normalizedEmail;
      const resolvedEmail = profile?.email ?? normalizedEmail;
      await addMember(room.id, resolvedEmail, resolvedUserId, 'member');
      setEmail('');
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <section className="card p-6">
      <h2 className="text-lg font-semibold mb-4">Members</h2>

      <div className="space-y-4 mb-6">
        {room.members.map((member) => (
          <MemberRow
            key={member.userId}
            member={member}
            profile={memberProfiles[resolveMemberProfileKey(member)]}
            isCurrentUser={member.userId === currentUserId}
            onRemove={() => removeMember(room.id, member.userId)}
            onRoleChange={(role) => changeMemberRole(room.id, member.userId, role)}
          />
        ))}
      </div>

      <div className="border-t border-border pt-4">
        <h3 className="text-sm font-medium text-gray-300 mb-3">Add Member</h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <Input
            placeholder="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1"
          />
          <Button
            onClick={handleAddMember}
            isLoading={isAdding}
            disabled={!email.trim()}
          >
            Add
          </Button>
        </div>
        {error && (
          <p className="text-danger text-sm mt-2">{error}</p>
        )}
      </div>
    </section>
  );
}

interface MemberRowProps {
  member: RoomMember;
  profile?: { displayName?: string; avatarUrl?: string };
  isCurrentUser: boolean;
  onRemove: () => void;
  onRoleChange: (role: RoomRole) => void;
}

/**
 * Single member row with role selector and remove button.
 */
function MemberRow({
  member,
  profile,
  isCurrentUser,
  onRemove,
  onRoleChange,
}: MemberRowProps) {
  const displayName = profile?.displayName;
  const fallbackName = displayName || member.email || 'Member';
  const avatarUrl = profile?.avatarUrl;

  return (
    <div className="flex items-center justify-between py-3 px-4 bg-surface-hover rounded-lg">
      <div className="flex items-center gap-3">
        <Avatar name={fallbackName} imageUrl={avatarUrl} size="md" />
        <div>
          <p className="font-medium">{fallbackName}</p>
          <p className="text-sm text-gray-400">{member.email}</p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <select
          value={member.role}
          onChange={(e) => onRoleChange(e.target.value as RoomRole)}
          disabled={isCurrentUser}
          className="bg-surface border border-border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-accent disabled:opacity-50"
        >
          <option value="member">Member</option>
          <option value="admin">Admin</option>
        </select>

        {!isCurrentUser && (
          <button
            onClick={onRemove}
            className="p-2 text-gray-400 hover:text-danger hover:bg-danger/10 rounded-lg transition-colors"
            title="Remove member"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Resolves the profile lookup key for a member.
 */
function resolveMemberProfileKey(member: RoomMember): string {
  if (!member.userId) {
    return member.email;
  }

  if (member.userId.startsWith('invited-') && member.email) {
    return member.email;
  }

  return member.userId;
}
